import React, { useState, useEffect } from "react";
import { getPerks, savePerks, deletePerk, clearCache } from "../../services/api";
import { Plus, Trash2, Save, MoveUp, MoveDown } from "lucide-react";

const PerksManagement = () => {
    const [perks, setPerks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newPerk, setNewPerk] = useState("");

    useEffect(() => {
        fetchPerks();
    }, []);

    const fetchPerks = async () => {
        try {
            setLoading(true);
            const res = await getPerks();
            if (res.data && res.data.success) {
                setPerks(res.data.data.sort((a, b) => a.order - b.order));
            }
        } catch (err) {
            console.error("Error fetching perks:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddPerk = () => {
        if (newPerk.trim()) {
            const updatedPerks = [
                ...perks,
                { text: newPerk, order: perks.length },
            ];
            setPerks(updatedPerks);
            setNewPerk("");
        }
    };

    const handleRemovePerk = async (index, id) => {
        await deletePerk(id);
        const updatedPerks = perks.filter((_, i) => i !== index);
        setPerks(updatedPerks);
    };

    const handleMove = (index, direction) => {
        const updatedPerks = [...perks];
        const newIndex = direction === "up" ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < updatedPerks.length) {
            [updatedPerks[index], updatedPerks[newIndex]] = [
                updatedPerks[newIndex],
                updatedPerks[index],
            ];
            // Update order property
            updatedPerks.forEach((p, i) => (p.order = i));
            setPerks(updatedPerks);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const res = await savePerks(perks);
            if (res.data && res.data.success) {
                clearCache("perks");
                alert("Membership perks saved successfully!");
                fetchPerks();
            }
        } catch (err) {
            console.error("Error saving perks:", err);
            alert("Failed to save perks");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading perks...</div>;

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
            <div className="flex justify-between items-center border-b pb-4">
                <h2 className="text-xl font-semibold text-gray-800">Membership Perks Management</h2>
                <p className="text-sm text-gray-500">These perks appear globally at the bottom of the Subscription page.</p>
            </div>

            <div className="space-y-4">
                <div className="flex space-x-2">
                    <input
                        type="text"
                        value={newPerk}
                        onChange={(e) => setNewPerk(e.target.value)}
                        className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Add a new perk (e.g., Free Delivery)"
                        onKeyDown={(e) => e.key === "Enter" && handleAddPerk()}
                    />
                    <button
                        onClick={handleAddPerk}
                        className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center space-x-2"
                    >
                        <Plus className="w-5 h-5" />
                        <span>Add</span>
                    </button>
                </div>

                <div className="space-y-2">
                    {perks.map((perk, index) => (
                        <div
                            key={index}
                            className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border group"
                        >
                            <div className="flex flex-col space-y-1">
                                <button
                                    onClick={() => handleMove(index, "up")}
                                    disabled={index === 0}
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                    <MoveUp className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleMove(index, "down")}
                                    disabled={index === perks.length - 1}
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                    <MoveDown className="w-4 h-4" />
                                </button>
                            </div>
                            <span className="flex-1 text-gray-700">{perk.text}</span>
                            <button
                                onClick={() => handleRemovePerk(index, perk._id)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        </div>
                    ))}
                    {perks.length === 0 && (
                        <div className="text-center py-8 text-gray-400">No perks added yet.</div>
                    )}
                </div>

                <div className="pt-4 border-t flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`flex items-center space-x-2 px-6 py-2 rounded-lg text-white font-medium ${saving ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                            }`}
                    >
                        <Save className="w-5 h-5" />
                        <span>{saving ? "Saving..." : "Save All Perks"}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PerksManagement;
